import { AdminAction } from '@/services/db/admin/adminActionTracking';
import { format } from 'date-fns';
import { getHeaderHtml, getFooterHtml, getCommonPrintStyles } from './printStyles';
import { downloadAsJPG, createPrintDocument, downloadAsPDF } from './printingBridge';
import { toast } from 'sonner';

export const printAdminActionHistoryAsJPG = async (
  actions: AdminAction[], 
  actionType: string = 'all'
) => {
  try {
    const loadingToast = toast.loading('Generating admin history JPG...');
    
    const { content, filename, styles } = generateAdminHistoryContent(actions, actionType);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admin history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admin history downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading admin history JPG:', error);
    toast.error('Failed to download admin history');
  }
};

export const printAdminActionHistoryAsPDF = async (
  actions: AdminAction[], 
  actionType: string = 'all'
) => {
  try {
    const loadingToast = toast.loading('Generating admin history PDF...');
    
    const { content, filename, styles } = generateAdminHistoryContent(actions, actionType);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admin history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admin history downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading admin history PDF:', error);
    toast.error('Failed to download admin history');
  }
};

const generateAdminHistoryContent = (actions: AdminAction[], actionType: string) => {
  const today = new Date();
  const formattedDate = format(today, 'M/d/yyyy');
  
  const title = `Admin Action History - ${actionType === 'all' ? 'All Actions' : actionType}`;
  const styles = getCommonPrintStyles() + `
    .history-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    .history-table th, .history-table td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    .history-table th {
      background-color: #f5f5f5;
    }
    .action-details {
      margin-top: 10px;
      padding: 10px;
      border: 1px solid #eee;
      background-color: #f9f9f9;
    }
  `;

  const tableRows = actions.map(action => `
    <tr>
      <td>${format(new Date(action.timestamp), 'MMM d, yyyy h:mm a')}</td>
      <td>${action.performedBy}</td>
      <td>${action.actionType}</td>
      <td>${action.description}</td>
    </tr>
  `).join('');

  const content = `
    <div class="page-content">
      ${getHeaderHtml(title)}
      <table class="history-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Performed By</th>
            <th>Action Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      ${getFooterHtml('admin')}
    </div>
  `;
  
  const filename = `Admin_Action_History_${actionType}_${formattedDate}`;
  
  return {
    content,
    filename,
    styles
  };
};

export const printStudentHistoryAsJPG = async (
  student: any,
  historyData: any[]
) => {
  try {
    const loadingToast = toast.loading('Generating student history JPG...');
    
    const { content, filename, styles } = generateStudentHistoryContent(student, historyData);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student history downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading student history:', error);
    toast.error('Failed to download student history');
  }
};

export const printStudentHistoryAsPDF = async (
  student: any,
  historyData: any[]
) => {
  try {
    const loadingToast = toast.loading('Generating student history PDF...');
    
    const { content, filename, styles } = generateStudentHistoryContent(student, historyData);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student history downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading student history:', error);
    toast.error('Failed to download student history');
  }
};

const generateStudentHistoryContent = (student: any, historyData: any[]) => {
  const today = new Date();
  const formattedDate = format(today, 'M/d/yyyy');
  
  const title = `Student History - ${student.surname ? `${student.name} ${student.surname}` : student.name}`;
  const styles = getCommonPrintStyles() + `
    .history-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    .history-table th, .history-table td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    .history-table th {
      background-color: #f5f5f5;
    }
    .student-info {
      margin-bottom: 20px;
      padding: 15px;
      background-color: #f9f9f9;
      border-radius: 8px;
    }
  `;

  const tableRows = historyData.map(item => `
    <tr>
      <td>${format(new Date(item.timestamp), 'MMM d, yyyy h:mm a')}</td>
      <td>${item.type}</td>
      <td>${item.description}</td>
      <td>${item.details}</td>
    </tr>
  `).join('');

  const content = `
    <div class="page-content">
      ${getHeaderHtml(title)}
      <div class="student-info">
        <h3>Student Information</h3>
        <p><strong>Name:</strong> ${student.surname ? `${student.name} ${student.surname}` : student.name}</p>
        <p><strong>ID:</strong> ${student.id}</p>
        <p><strong>Programs:</strong> ${student.programs?.map(p => p.title).join(', ') || 'None'}</p>
      </div>
      <table class="history-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Type</th>
            <th>Description</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      ${getFooterHtml('student')}
    </div>
  `;
  
  const filename = `Student_History_${student.surname ? `${student.name}_${student.surname}` : student.name}_${formattedDate}`;
  
  return {
    content,
    filename,
    styles
  };
};

export const printTeacherHistoryAsJPG = async (
  teacher: any,
  paymentData: any[]
) => {
  try {
    const loadingToast = toast.loading('Generating teacher history JPG...');
    
    const { content, filename, styles } = generateTeacherHistoryContent(teacher, paymentData);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download teacher history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Teacher history downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading teacher history:', error);
    toast.error('Failed to download teacher history');
  }
};

export const printTeacherHistoryAsPDF = async (
  teacher: any,
  paymentData: any[]
) => {
  try {
    const loadingToast = toast.loading('Generating teacher history PDF...');
    
    const { content, filename, styles } = generateTeacherHistoryContent(teacher, paymentData);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download teacher history');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Teacher history downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading teacher history:', error);
    toast.error('Failed to download teacher history');
  }
};

const generateTeacherHistoryContent = (teacher: any, paymentData: any[]) => {
  const today = new Date();
  const formattedDate = format(today, 'M/d/yyyy');
  
  // Use idNumber for display (the simple numeric ID like 1, 2, 3) instead of the UUID id
  const displayId = teacher.idNumber && teacher.idNumber.trim() !== '' ? teacher.idNumber : 'N/A';
  
  const title = `Teacher History - ${teacher.firstName} ${teacher.lastName}`;
  const styles = getCommonPrintStyles() + `
    .history-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    .history-table th, .history-table td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    .history-table th {
      background-color: #f5f5f5;
    }
    .teacher-info {
      margin-bottom: 20px;
      padding: 15px;
      background-color: #f9f9f9;
      border-radius: 8px;
    }
  `;

  const tableRows = paymentData.map(payment => `
    <tr>
      <td>${format(new Date(payment.date), 'MMM d, yyyy')}</td>
      <td>AFN ${payment.amount.toFixed(2)}</td>
      <td>${payment.paymentPeriod}</td>
      <td>${payment.method}</td>
      <td>${payment.status}</td>
    </tr>
  `).join('');

  const content = `
    <div class="page-content">
      ${getHeaderHtml(title)}
      <div class="teacher-info">
        <h3>Teacher Information</h3>
        <p><strong>Name:</strong> ${teacher.firstName} ${teacher.lastName}</p>
        <p><strong>ID:</strong> ${displayId}</p>
        <p><strong>Department:</strong> ${teacher.department || 'Not Assigned'}</p>
        <p><strong>Subjects:</strong> ${teacher.subjects?.join(', ') || 'None'}</p>
      </div>
      <table class="history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Amount</th>
            <th>Period</th>
            <th>Method</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      ${getFooterHtml('teacher')}
    </div>
  `;
  
  const filename = `Teacher_History_${teacher.firstName}_${teacher.lastName}_${formattedDate}`;
  
  return {
    content,
    filename,
    styles
  };
};
